import { Segmented } from '@/components/admin/experiences/page-editor/segmented';
import type {
  BlockAlign,
  BlockSpacing,
  BlockVisual,
  BlockWidth,
  ExperienceScale,
  TextColorToken,
} from '@matter_extractor/shared';
import { EMPTY_BLOCK_VISUAL } from '@matter_extractor/shared';
import { Button } from '@pms-digital/design-system';

/**
 * L'onglet **Visuel** d'un bloc : ses jetons de surcharge (refonte modulaire
 * §9.4; planche `Inspecteurs`; issue #58).
 *
 * §9.4 has three levels - the experience, the page, the block - and the block's
 * is the narrowest: « alignement, taille, couleur du texte seulement ». The
 * schema carries five (`blockVisualSchema`), because an image's WIDTH and its
 * spacing are the same kind of thing and the maquette puts them here too: « un
 * champ montré deux fois n'est pas deux champs stockés deux fois ».
 *
 * ## « Hérité » est une VALEUR, et c'est `null`
 *
 * `blockVisualSchema` says it in as many words: « null is hérité and is the only
 * spelling of it ». So every control here has a first segment meaning « hérité »
 * rather than a checkbox beside it, and « Revenir au global » clears all five
 * at once - which is the gesture §9.4 names.
 */

const ALIGNS: readonly { value: BlockAlign | 'inherit'; label: string }[] = [
  { value: 'inherit', label: 'Hérité' },
  { value: 'left', label: 'Gauche' },
  { value: 'center', label: 'Centré' },
  { value: 'right', label: 'Droite' },
];

const WIDTHS: readonly { value: BlockWidth | 'inherit'; label: string }[] = [
  { value: 'inherit', label: 'Hérité' },
  { value: 'full', label: 'Pleine' },
  { value: 'content', label: 'Au contenu' },
];

const SPACINGS: readonly { value: BlockSpacing | 'inherit'; label: string }[] = [
  { value: 'inherit', label: 'Hérité' },
  { value: 'tight', label: 'Serré' },
  { value: 'normal', label: 'Normal' },
  { value: 'loose', label: 'Aéré' },
];

const SCALES: readonly { value: ExperienceScale | 'inherit'; label: string }[] = [
  { value: 'inherit', label: 'Hérité' },
  { value: 'small', label: 'Petit' },
  { value: 'normal', label: 'Normal' },
  { value: 'large', label: 'Grand' },
];

const COLORS: readonly { value: TextColorToken | 'inherit'; label: string }[] = [
  { value: 'inherit', label: 'Hérité' },
  { value: 'text', label: 'Texte' },
  { value: 'muted', label: 'Discret' },
  { value: 'accent', label: 'Accent' },
];

const LABEL = 'block font-semibold text-gray-800 text-caption';

export interface VisualPanelProps {
  readonly visual: BlockVisual;
  readonly readOnly: boolean;
  /** Only a Texte block scales and colours its words; the rest do not (§9.4). */
  readonly textual: boolean;
  readonly onChange: (next: BlockVisual) => void;
}

export function VisualPanel({ visual, readOnly, textual, onChange }: VisualPanelProps) {
  const overridden = Object.values(visual).some((token) => token !== null);
  const read = <Value extends string>(value: Value | null): Value | 'inherit' =>
    value === null ? 'inherit' : value;
  const write = <Value extends string>(next: Value | 'inherit'): Value | null =>
    next === 'inherit' ? null : next;

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <p className={LABEL}>Alignement</p>
        <Segmented
          label="Alignement du bloc"
          size="sm"
          value={read(visual.align)}
          options={ALIGNS}
          readOnly={readOnly}
          onChange={(align) => onChange({ ...visual, align: write<BlockAlign>(align) })}
        />
      </div>

      <div className="space-y-1.5">
        <p className={LABEL}>Largeur</p>
        <Segmented
          label="Largeur du bloc"
          size="sm"
          value={read(visual.width)}
          options={WIDTHS}
          readOnly={readOnly}
          onChange={(width) => onChange({ ...visual, width: write<BlockWidth>(width) })}
        />
      </div>

      <div className="space-y-1.5">
        <p className={LABEL}>Espacement</p>
        <Segmented
          label="Espacement du bloc"
          size="sm"
          value={read(visual.spacing)}
          options={SPACINGS}
          readOnly={readOnly}
          onChange={(spacing) => onChange({ ...visual, spacing: write<BlockSpacing>(spacing) })}
        />
      </div>

      {textual ? (
        <>
          <div className="space-y-1.5">
            <p className={LABEL}>Taille du texte</p>
            <Segmented
              label="Taille du texte"
              size="sm"
              value={read(visual.textScale)}
              options={SCALES}
              readOnly={readOnly}
              onChange={(textScale) =>
                onChange({ ...visual, textScale: write<ExperienceScale>(textScale) })
              }
            />
            <p className="text-gray-600 text-caption leading-4">
              « Grand » est le style de titre du thème : c’est le seul chemin de « ce bloc est un
              titre » à un titre à l’écran (§9.4).
            </p>
          </div>

          <div className="space-y-1.5">
            <p className={LABEL}>Couleur du texte</p>
            <Segmented
              label="Couleur du texte"
              size="sm"
              value={read(visual.textColor)}
              options={COLORS}
              readOnly={readOnly}
              onChange={(textColor) =>
                onChange({ ...visual, textColor: write<TextColorToken>(textColor) })
              }
            />
          </div>
        </>
      ) : null}

      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={readOnly || !overridden}
        onClick={() => onChange(EMPTY_BLOCK_VISUAL)}
      >
        Revenir au visuel de la page
      </Button>
    </div>
  );
}
